// Report Builder template CRUD — see docs/Report-Builder-Implementation-Plan.md.
//
// The stock "Weekly Report" is code-defined (STOCK_WEEKLY_TEMPLATE, id null,
// no DB row). An org that customises it gets a copy-on-write override row
// with the well-known slug 'weekly-report' which shadows the stock entry in
// listTemplates(); deleting that row is surfaced as "Reset to default".

import api from '../api/axios';
import {
  ReportTemplate,
  ReportTemplateConfig,
  STOCK_WEEKLY_TEMPLATE,
  WEEKLY_REPORT_SLUG,
} from '../models/reportTemplate';

interface SaveTemplateRequest {
  name: string;
  config: ReportTemplateConfig;
  /** Only 'weekly-report' is meaningful — creates the stock override row. */
  slug?: string;
}

export class ReportTemplateDataService {
  /**
   * All templates the org can generate from: the stock Weekly Report first
   * (shadowed by the org's 'weekly-report' override row when one exists),
   * then org-created custom templates. The first entry is always the valid
   * default dropdown selection.
   */
  static async listTemplates(): Promise<ReportTemplate[]> {
    const response = await api.get('/report-templates');
    const orgRows = response.data.templates as ReportTemplate[];
    const override = orgRows.find((t) => t.slug === WEEKLY_REPORT_SLUG);
    const customs = orgRows.filter((t) => t.slug !== WEEKLY_REPORT_SLUG);
    return [override ?? STOCK_WEEKLY_TEMPLATE, ...customs];
  }

  static async getTemplate(id: number): Promise<ReportTemplate> {
    const response = await api.get(`/report-templates/${id}`);
    return response.data as ReportTemplate;
  }

  /** Create a custom template — or the stock override when slug is 'weekly-report'. */
  static async createTemplate(request: SaveTemplateRequest): Promise<ReportTemplate> {
    const response = await api.post('/report-templates', request);
    return response.data as ReportTemplate;
  }

  static async updateTemplate(
    id: number,
    request: { name: string; config: ReportTemplateConfig }
  ): Promise<ReportTemplate> {
    const response = await api.put(`/report-templates/${id}`, request);
    return response.data as ReportTemplate;
  }

  /** Delete a custom template. On the 'weekly-report' override this is "Reset to default". */
  static async deleteTemplate(id: number): Promise<void> {
    await api.delete(`/report-templates/${id}`);
  }
}
